import { createContext, useContext, useState, useEffect } from "react";
import { useNavigate  } from "react-router-dom";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

  useEffect(() => {

    const checkAuthStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/current_user`, {
          method: "GET",
          credentials: "include",
        });

        const data = await response.json();

        if (response.ok) {
          setCurrentUser(data);
        } else {
          console.error("Authentication failed:", data);
          setCurrentUser(null);
        }
      } catch (error) {
        console.error("Error during authentication:", error);
        setCurrentUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    checkAuthStatus();
  }, []);

  const updateUserPaymentStatus = async () => {
    setCurrentUser((prev) => ({
      ...prev,
      profile: { ...prev.profile, ctg_course_paid: true }
    }));
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/users/sign_in`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ user: { login: email, password } }),
      });

      const data = await response.json();

      if (response.ok) {
        console.log("Login successful:", data);
        setCurrentUser(data);
        return { success: true };
      } else {
        console.error("Login failed:", data.error);
        return { success: false, message: data.error };
      }
    } catch (error) {
      console.error("Error during login:", error);
      return { success: false, message: "Network error or other issue during login." };
    }
  };

  const logout = async () => {
    try {
      const response = await fetch(`${API_URL}/users/sign_out`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await response.json();

      if (response.ok) {
        console.log("Logout successful:", data);
        setCurrentUser(null);
        navigate("/");
      } else {
        console.error("Logout failed:", data);
      }
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await fetch(`${API_URL}/api/v1/profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ profile: profileData }),
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentUser((prev) => ({ ...prev, profile: data.profile }));
      } else {
        console.error("Failed to update profile");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  const submitTest = async (testResponses, sectionSlug, testType, sectionId) => {
    try {
      const response = await fetch(`${API_URL}/api/v1/sections/${sectionSlug}/${testType}`, {
        method: "POST",
        headers: { "Content-Type": "application/json"},
        credentials: "include",
        body: JSON.stringify({ section_id: sectionId, responses: testResponses }),
      });

      const data = await response.json();

      if (response.ok) {
        console.log("Test submitted succesffully")
        return { success: true, test: data.test };
      } else {
        console.error("Failed to submit test");
      }
    } catch (error) {
      console.error("Error submitting test:", error);
    }
  };

  const searchUsers = async (searchType, searchTerm) => {
    try {
      const response = await fetch(`${API_URL}/api/v1/admin_dashboard/paid_user_search`, {
        method: "POST",
        headers: { "Content-Type": "application/json"},
        credentials: "include",
        body: JSON.stringify({ search_type: searchType, search_term: searchTerm }),
      });

      const data = await response.json();

      if (response.ok) {
        console.log("Search completed successfully")
        return { success: true, paid_users: data };
      } else {
        console.error("Search failed");
      }
    } catch (error) {
      console.error("Error conducting search:", error);
    }
  };

  const searchInvitableUsers = async (searchType, searchTerm) => {
    try {
      const response = await fetch(`${API_URL}/api/v1/admin_dashboard/invitation_search`, {
        method: "POST",
        headers: { "Content-Type": "application/json"},
        credentials: "include",
        body: JSON.stringify({ search_type: searchType, search_term: searchTerm }),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, invitable_users: data };
      } else {
        console.error("Search failed:", data);
        return { success: false, message: data.errors?.join(", ") || "Search failed." };
      }
    } catch (error) {
      console.error("Error conducting search:", error);
      return { success: false, message: "Network error conducting search." };
    }
  };

  const value = { currentUser, isLoading, login, logout, updateUserPaymentStatus, updateProfile, submitTest, searchUsers, searchInvitableUsers };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
